import { ApiResponse } from '@/types';
import { coreApiClient } from './core';
import type {
    ExamKind,
    ExamPlan,
    SetupStepKey,
    SetupAnswers,
    SetupIntent,
    SetupWizardState,
} from '@/features/Setup/types';

interface StepResponse {
    next_step: SetupStepKey | null;
    progress: number;
    intent: SetupIntent | null;
}

interface ConfirmResponse {
    reopen: boolean;
    next_step?: SetupStepKey;
    intent: SetupIntent | null;
}

const fail = (e: unknown, msg: string) => ({
    success: false as const,
    error: e instanceof Error ? e.message : msg,
});

/**
 * The unified setup flow.
 *
 * The wizard reads its own state from GET /setup — it runs on the first
 * launch, before there is a dashboard worth fetching. The card's prompt still
 * rides along on the dashboard and profile payloads as a `setup` key, so the
 * home screen costs one round trip, not two.
 *
 * There is no reset call: replaying is `php artisan setup:reset <user>`, which
 * nulls setup_finished_at and lets the next launch do the rest.
 *
 * docs/exam-intents/audience-collection-plan.md §8
 */
export const setupApi = {
    /** Everything the wizard renders. */
    async get(): Promise<ApiResponse<SetupWizardState>> {
        try {
            const res = await coreApiClient.axios.get<ApiResponse<SetupWizardState>>('/api/setup');
            return res.data;
        } catch (e) {
            return fail(e, 'Failed to load setup');
        }
    },

    /** Called after EVERY question: a drop-off still leaves data. */
    async submitStep(
        step: SetupStepKey,
        surface: string,
        answers?: SetupAnswers,
    ): Promise<ApiResponse<StepResponse>> {
        try {
            const res = await coreApiClient.axios.post<ApiResponse<StepResponse>>(
                '/api/setup/step',
                { step, surface, answers },
            );
            return res.data;
        } catch (e) {
            return fail(e, 'Failed to save your answer');
        }
    },

    async skipStep(step: SetupStepKey, surface: string): Promise<ApiResponse<{ next_step: SetupStepKey | null }>> {
        try {
            const res = await coreApiClient.axios.post<ApiResponse<{ next_step: SetupStepKey | null }>>(
                '/api/setup/skip',
                { step, surface },
            );
            return res.data;
        } catch (e) {
            return fail(e, 'Failed to skip');
        }
    },

    /**
     * The flow is done with — reached its closing screen, or walked away from.
     * Both mean the same thing, so both call this: the wizard is offered once,
     * and declining to answer is an answer.
     */
    async finish(): Promise<ApiResponse<void>> {
        try {
            const res = await coreApiClient.axios.post<ApiResponse<void>>('/api/setup/finish');
            return res.data;
        } catch (e) {
            return fail(e, 'Failed to close setup');
        }
    },

    /**
     * One tap to re-assert an expired answer. The card no longer sends
     * `changed: true` — it opens the Profile editor for that exam instead.
     */
    async confirm(changed: boolean, exam?: ExamKind): Promise<ApiResponse<ConfirmResponse>> {
        try {
            const res = await coreApiClient.axios.post<ApiResponse<ConfirmResponse>>(
                '/api/setup/confirm',
                { changed, exam },
            );
            return res.data;
        } catch (e) {
            return fail(e, 'Failed to confirm');
        }
    },

    /**
     * Post-exam result. Writes the same survey_responses row the Telegram bot
     * would have, so answering here suppresses the bot's question too.
     */
    async submitExamResult(params: {
        exam: ExamKind;
        /** The sitting group, so the server can derive where the row belongs. */
        group?: string;
        survey_id: string;
        result: string;
        exam_slug?: string | null;
    }): Promise<ApiResponse<void>> {
        try {
            const res = await coreApiClient.axios.post<ApiResponse<void>>('/api/setup/exam-result', params);
            return res.data;
        } catch (e) {
            return fail(e, 'Failed to save your result');
        }
    },

    /**
     * "Are you sitting the next one?", asked straight after a result that was
     * not a pass. Writes the intent only — never a survey row — so it can never
     * silence the results question it follows.
     */
    async submitExamPlan(params: { exam: ExamKind; plan: ExamPlan }): Promise<ApiResponse<{ intent: SetupIntent | null }>> {
        try {
            const res = await coreApiClient.axios.post<ApiResponse<{ intent: SetupIntent | null }>>(
                '/api/setup/exam-plan',
                params,
            );
            return res.data;
        } catch (e) {
            return fail(e, 'Failed to save your plan');
        }
    },

};
